/* eslint-disable no-undef */
import React, { useEffect, useState, useCallback } from 'react';
import { toast } from 'react-toastify';
import { sendRequest } from '../../../utils/dateHelpers';
import { format } from 'date-fns';
import PageTitle from '../../../components/PageTitle';
import Select from 'react-select';
import { getPlanPricing } from '../../../utils/pricingV2';
import PlanStatusModal from '../../../components/Payment/PlanStatusModal';
import Loading from '../../../components/Loading';
import { Skeleton } from '../../../components/ui';

/* ================= API ENDPOINTS ================= */
const API_PROFILE_URL = '/billing/profile';
const API_SAVE_ADDRESS_URL = '/billing/profile/address';
const API_PLANS_URL = '/billing/plans';
const API_PLAN_CHECKOUT_URL = '/billing/checkout';
const API_PLAN_CHANGE_URL = '/billing/change-plan';
const API_CHECKOUT_PREVIEW_URL = '/billing/checkout-preview';
const API_USAGE_URL = '/billing/usage';
const CHECKOUT_PLAN_STORAGE_KEY = 'billingCheckoutPlanId';
const CHECKOUT_INTERVAL_STORAGE_KEY = 'billingCheckoutInterval';
let paddleInitialized = false;

const COUNTRY_CODES = `AF AL DZ AS AD AO AI AQ AG AR AM AW AU AT AZ BS BH BD BB BY BE BZ BJ BM BT BO BQ BA BW BV BR IO BN BG BF BI CV KH CM CA KY CF TD CL CN CX CC CO KM CG CD CK CR CI HR CU CW CY CZ DK DJ DM DO EC EG SV GQ ER EE SZ ET FK FO FJ FI FR GF PF TF GA GM GE DE GH GI GR GL GD GP GU GT GG GN GW GY HT HM VA HN HK HU IS IN ID IR IQ IE IM IL IT JM JP JE JO KZ KE KI KP KR KW KG LA LV LB LS LR LY LI LT LU MO MG MW MY MV ML MT MH MQ MR MU YT MX FM MD MC MN ME MS MA MZ MM NA NR NP NL NC NZ NI NE NG NU NF MK MP NO OM PK PW PS PA PG PY PE PH PN PL PT PR QA RE RO RU RW BL SH KN LC MF PM VC WS SM ST SA SN RS SC SL SG SX SK SI SB SO ZA GS SS ES LK SD SR SJ SE CH SY TW TJ TZ TH TL TG TK TO TT TN TR TM TC TV UG UA AE GB US UM UY UZ VU VE VN VG VI WF EH YE ZM ZW XK`;

const countryDisplayNames =
  typeof Intl !== 'undefined' && typeof Intl.DisplayNames === 'function'
    ? new Intl.DisplayNames(['en'], { type: 'region' })
    : null;

const COUNTRY_OPTIONS = COUNTRY_CODES.split(' ')
  .map(code => ({
    value: code,
    label: code === 'XK' ? 'Kosovo' : countryDisplayNames?.of(code) || code
  }))
  .sort((a, b) => a.label.localeCompare(b.label));

const COUNTRY_SELECT_STYLES = {
  control: (base, state) => ({
    ...base,
    minHeight: 42,
    border: `2px solid ${state.isFocused ? '#3b82f6' : '#cbd5e1'}`,
    borderRadius: '0.5rem',
    boxShadow: state.isFocused ? '0 0 0 1px #3b82f6' : 'none',
    backgroundColor: '#ffffff',
    '&:hover': {
      borderColor: state.isFocused ? '#3b82f6' : '#94a3b8'
    }
  }),
  valueContainer: base => ({ ...base, padding: '2px 12px' }),
  input: base => ({ ...base, color: '#0f172a' }),
  singleValue: base => ({ ...base, color: '#0f172a' }),
  placeholder: base => ({ ...base, color: '#64748b' }),
  menu: base => ({
    ...base,
    zIndex: 50,
    backgroundColor: '#ffffff',
    border: '1px solid #cbd5e1',
    boxShadow: '0 10px 25px rgba(15, 23, 42, 0.15)'
  }),
  menuList: base => ({ ...base, padding: '4px' }),
  option: (base, state) => ({
    ...base,
    borderRadius: '0.375rem',
    color: '#0f172a',
    backgroundColor: state.isSelected
      ? '#dbeafe'
      : state.isFocused
        ? '#eff6ff'
        : '#ffffff',
    '&:active': { backgroundColor: '#dbeafe' }
  }),
  indicatorSeparator: base => ({ ...base, backgroundColor: '#cbd5e1' })
};

export const getRequestedPaidPlanId = search => {
  const requestedPlanId = new URLSearchParams(search).get('plan_id');
  return ['1', '2', '3', '4'].includes(requestedPlanId)
    ? requestedPlanId
    : null;
};

export const getRequestedBillingInterval = search => {
  const interval = new URLSearchParams(search).get('billing_interval');
  return interval === 'year' ? 'year' : 'month';
};

export const getRequestedPaidPlan = (plans, search) => {
  const requestedPlanId = getRequestedPaidPlanId(search);
  if (!requestedPlanId) return null;

  return (
    plans.find(plan => String(plan.plan_id) === String(requestedPlanId)) || null
  );
};

export const rememberCheckoutPlanId = (
  storage,
  planId,
  billingInterval = 'month'
) => {
  if (!storage || !['1', '2', '3', '4'].includes(String(planId))) return;
  try {
    storage.setItem(CHECKOUT_PLAN_STORAGE_KEY, String(planId));
    storage.setItem(
      CHECKOUT_INTERVAL_STORAGE_KEY,
      billingInterval === 'year' ? 'year' : 'month'
    );
  } catch (error) {
    console.error('Checkout plan storage error:', error);
  }
};

export const resolveRequestedBillingInterval = (search, storage) => {
  const params = new URLSearchParams(search);
  if (params.has('billing_interval'))
    return getRequestedBillingInterval(search);
  if (!params.has('_ptxn') || !storage) return 'month';
  try {
    return storage.getItem(CHECKOUT_INTERVAL_STORAGE_KEY) === 'year'
      ? 'year'
      : 'month';
  } catch (error) {
    console.error('Checkout interval storage error:', error);
    return 'month';
  }
};

export const resolveRequestedPaidPlanId = (search, storage) => {
  const requestedPlanId = getRequestedPaidPlanId(search);
  if (requestedPlanId) return requestedPlanId;
  if (!new URLSearchParams(search).has('_ptxn') || !storage) return null;
  try {
    const storedPlanId = storage.getItem(CHECKOUT_PLAN_STORAGE_KEY);
    return ['1', '2', '3', '4'].includes(storedPlanId) ? storedPlanId : null;
  } catch (error) {
    console.error('Checkout plan storage error:', error);
    return null;
  }
};

export const getPaddleInitializationOptions = ({ clientToken, sellerId }) => {
  if (clientToken) return { token: clientToken };
  if (sellerId) return { seller: Number(sellerId) };
  return null;
};

const initializePaddleCheckout = () => {
  if (paddleInitialized || !window.Paddle) return paddleInitialized;
  // CRA, build sırasında NODE_ENV'i her zaman 'production' yapar; staging build'ini
  // prod'dan ayıran REACT_APP_ENV'dir (.env.staging / .env.production).
  const production =
    process.env.NODE_ENV === 'production' &&
    process.env.REACT_APP_ENV === 'production';
  const options = getPaddleInitializationOptions({
    clientToken: process.env.REACT_APP_PADDLE_CLIENT_TOKEN,
    sellerId: production
      ? process.env.REACT_APP_PADDLE_SELLER_ID
      : process.env.REACT_APP_PADDLE_SELLER_ID_SANDBOX
  });
  if (!options) {
    console.error('Paddle client token or seller ID is not configured.');
    return false;
  }
  if (!production) {
    window.Paddle.Environment.set('sandbox');
  }
  window.Paddle.Initialize(options);
  paddleInitialized = true;
  return true;
};

export const isBillingAddressComplete = billingDetails =>
  Boolean(
    billingDetails?.first_line &&
    billingDetails?.city &&
    billingDetails?.postal_code
  );

export const formatOutstandingAmount = amountCents =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD'
  }).format(Number(amountCents || 0) / 100);

export const isUsableCheckoutPreview = preview => {
  if (!preview?.success) return false;
  const amountFields = ['subtotal_cents', 'tax_cents', 'total_cents'];
  if (
    amountFields.some(
      field =>
        preview[field] === null ||
        preview[field] === undefined ||
        !Number.isFinite(Number(preview[field])) ||
        Number(preview[field]) < 0
    )
  ) {
    return false;
  }
  return preview.kind !== 'new_checkout' || Number(preview.total_cents) > 0;
};

export const isRequestedPlanActive = (
  usage,
  planId,
  billingInterval,
  offerId = null
) =>
  String(usage?.plan_id) === String(planId) &&
  (usage?.billing_interval || 'month') === billingInterval &&
  (String(planId) !== '4' ||
    (Boolean(offerId) && usage?.enterprise_offer_id === offerId));

export const clearCheckoutSuccessState = () => {
  const url = new URL(window.location.href);
  ['checkout', 'plan_id', 'billing_interval', 'offer_id', '_ptxn'].forEach(
    parameter => url.searchParams.delete(parameter)
  );
  window.history.replaceState(
    window.history.state,
    '',
    `${url.pathname}${url.search}${url.hash}`
  );
};

const CardManagement = () => {
  const [initialLoading, setInitialLoading] = useState(true);
  const [buttonLoading, setButtonLoading] = useState(false);
  const [addressLoading, setAddressLoading] = useState(false);
  const [hasCard, setHasCard] = useState(false);
  const [completedAt, setCompletedAt] = useState();
  const [portalUrl, setPortalUrl] = useState('');
  const [paymentMethodUpdateUrl, setPaymentMethodUpdateUrl] = useState('');
  const [billingState, setBillingState] = useState({});
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [activeSubscription, setActiveSubscription] = useState(null);
  const [checkoutPreview, setCheckoutPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState('');
  const [isBillingSaved, setIsBillingSaved] = useState(false);
  const [checkoutConfirmed, setCheckoutConfirmed] = useState(false);
  const [checkoutHandled, setCheckoutHandled] = useState(false);
  const [pendingPlanActivation, setPendingPlanActivation] = useState(null);
  const [planStatusModal, setPlanStatusModal] = useState(null);
  const queryParams = new URLSearchParams(window.location.search);
  const checkoutSubmitted = queryParams.get('checkout') === 'success';
  const paddleTransactionId = queryParams.get('_ptxn');
  const requestedPlanId = resolveRequestedPaidPlanId(
    window.location.search,
    window.sessionStorage
  );
  const requestedBillingInterval =
    selectedPlan?.enterprise_offer?.billing_interval ||
    resolveRequestedBillingInterval(
      window.location.search,
      window.sessionStorage
    );
  const requestedOfferId =
    queryParams.get('offer_id') ||
    (paddleTransactionId
      ? window.sessionStorage.getItem('billingCheckoutOfferId')
      : null);

  // Fatura Adresi State'i
  const [billingDetails, setBillingDetails] = useState({
    country_code: 'TR',
    postal_code: '',
    region: '',
    city: '',
    first_line: '',
    tax_identifier: '',
    company_name: ''
  });

  const handleInputChange = e => {
    const { name, value } = e.target;
    setBillingDetails(prev => ({ ...prev, [name]: value }));
    setIsBillingSaved(false);
  };

  /* ================= FETCH BILLING ADDRESS ================= */

  const fetchStatus = useCallback(async () => {
    setInitialLoading(true);
    const [profileResult, plansResult, usageResult] = await Promise.allSettled([
      sendRequest(API_PROFILE_URL, 'GET'),
      sendRequest(API_PLANS_URL, 'GET'),
      sendRequest(API_USAGE_URL, 'GET')
    ]);

    try {
      if (profileResult.status === 'fulfilled') {
        const profile = profileResult.value;
        const details = profile?.billing_details || {};
        setBillingDetails({
          country_code: details.country_code || 'TR',
          postal_code: details.postal_code || '',
          region: details.region || '',
          city: details.city || '',
          first_line: details.first_line || '',
          tax_identifier: details.tax_identifier || '',
          company_name: details.company_name || ''
        });
        setIsBillingSaved(isBillingAddressComplete(details));
        setPortalUrl(profile?.portal_url || '');
        setPaymentMethodUpdateUrl(
          profile?.payment_method_update_url || profile?.portal_url || ''
        );
        setBillingState(profile?.billing_state || {});
        setActiveSubscription(profile?.subscription || null);
        setHasCard(Boolean(profile?.setup?.payment_configured));
        setCompletedAt(profile?.subscription?.current_period_start);
      } else {
        console.error('Billing profile error:', profileResult.reason);
      }

      if (plansResult.status === 'fulfilled') {
        const plansResponse = plansResult.value;
        const availablePlans = plansResponse?.plans || plansResponse || [];
        setSelectedPlan(
          availablePlans.find(
            plan => String(plan.plan_id) === String(requestedPlanId)
          ) || null
        );
      } else {
        console.error('Billing plans error:', plansResult.reason);
      }

      if (usageResult.status === 'fulfilled') {
        const usageResponse = usageResult.value;
        setCheckoutConfirmed(
          checkoutSubmitted &&
            isRequestedPlanActive(
              usageResponse?.usage,
              requestedPlanId,
              requestedBillingInterval,
              requestedOfferId
            )
        );
      } else {
        console.error('Billing usage error:', usageResult.reason);
      }

      if (
        [profileResult, plansResult, usageResult].every(
          result => result.status === 'rejected'
        )
      ) {
        toast.error('Failed to load payment status.');
      }
    } finally {
      setInitialLoading(false);
    }
  }, [
    checkoutSubmitted,
    requestedBillingInterval,
    requestedPlanId,
    requestedOfferId
  ]);

  const loadCheckoutPreview = useCallback(async () => {
    if (!requestedPlanId || !selectedPlan || !isBillingSaved) {
      setCheckoutPreview(null);
      return;
    }
    setPreviewLoading(true);
    setPreviewError('');
    try {
      const preview = await sendRequest(API_CHECKOUT_PREVIEW_URL, 'POST', {
        plan_id: requestedPlanId,
        billing_interval: requestedBillingInterval,
        ...(requestedPlanId === '4' ? { offer_id: requestedOfferId } : {})
      });
      if (!isUsableCheckoutPreview(preview)) {
        throw new Error(
          'Paddle did not return a valid amount. Please retry the calculation.'
        );
      }
      setCheckoutPreview(preview);
    } catch (error) {
      setCheckoutPreview(null);
      setPreviewError(
        error.message || 'The amount due could not be calculated. Please retry.'
      );
    } finally {
      setPreviewLoading(false);
    }
  }, [
    isBillingSaved,
    requestedBillingInterval,
    requestedPlanId,
    selectedPlan,
    requestedOfferId
  ]);

  /* ================= SAVE BILLING ADDRESS ================= */

  const handleSaveAddress = async () => {
    if (
      !billingDetails.first_line ||
      !billingDetails.city ||
      !billingDetails.postal_code
    ) {
      toast.warning(
        'Please fill in the required fields (Address, City, Postal Code).'
      );
      return;
    }

    setAddressLoading(true);
    try {
      await sendRequest(API_SAVE_ADDRESS_URL, 'PUT', billingDetails);
      toast.success('Billing address saved successfully.');
      setIsBillingSaved(true);
    } catch (err) {
      console.error('Save address error:', err);
      toast.error('An error occurred while saving the address.');
    } finally {
      setAddressLoading(false);
    }
  };

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  useEffect(() => {
    loadCheckoutPreview();
  }, [loadCheckoutPreview]);

  useEffect(() => {
    if (!paddleTransactionId) return undefined;
    if (initializePaddleCheckout()) return undefined;
    const timer = window.setInterval(() => {
      if (initializePaddleCheckout()) window.clearInterval(timer);
    }, 250);
    const timeout = window.setTimeout(() => window.clearInterval(timer), 10000);
    return () => {
      window.clearInterval(timer);
      window.clearTimeout(timeout);
    };
  }, [paddleTransactionId]);

  useEffect(() => {
    const activationTarget =
      pendingPlanActivation ||
      (checkoutSubmitted && !checkoutHandled && requestedPlanId
        ? {
            planId: requestedPlanId,
            billingInterval: requestedBillingInterval,
            offerId: requestedOfferId,
            planName: selectedPlan?.plan_name || 'Selected'
          }
        : null);

    if (
      !activationTarget ||
      checkoutConfirmed ||
      planStatusModal?.status === 'active'
    ) {
      return undefined;
    }
    const confirmActivation = async () => {
      try {
        const response = await sendRequest(API_USAGE_URL, 'GET');
        if (
          isRequestedPlanActive(
            response?.usage,
            activationTarget.planId,
            activationTarget.billingInterval,
            activationTarget.offerId
          )
        ) {
          if (checkoutSubmitted) {
            setCheckoutConfirmed(true);
          } else {
            setPendingPlanActivation(null);
            setPlanStatusModal({
              status: 'active',
              planName: activationTarget.planName
            });
            fetchStatus();
          }
        }
      } catch (error) {
        console.error('Subscription confirmation error:', error);
      }
    };
    confirmActivation();
    const timer = window.setInterval(confirmActivation, 5000);
    return () => window.clearInterval(timer);
  }, [
    checkoutConfirmed,
    checkoutHandled,
    checkoutSubmitted,
    fetchStatus,
    pendingPlanActivation,
    planStatusModal?.status,
    requestedBillingInterval,
    requestedPlanId,
    requestedOfferId,
    selectedPlan?.plan_name
  ]);

  useEffect(() => {
    if (!checkoutConfirmed || checkoutHandled || !selectedPlan) return;
    setPlanStatusModal({
      status: 'active',
      planName: selectedPlan.plan_name,
      checkout: true
    });
  }, [checkoutConfirmed, checkoutHandled, selectedPlan]);

  /* ================= INITIATE CHECKOUT ================= */

  const openCardCheckout = async () => {
    if (
      buttonLoading ||
      !requestedPlanId ||
      !isBillingSaved ||
      !checkoutPreview
    ) {
      return;
    }
    setButtonLoading(true);

    if (!isBillingAddressComplete(billingDetails)) {
      toast.warning(
        'Please complete and save your billing address before continuing.'
      );
      setButtonLoading(false);
      return;
    }

    try {
      const requestBody = {
        plan_id: requestedPlanId,
        billing_interval: requestedBillingInterval,
        ...(requestedPlanId === '4' ? { offer_id: requestedOfferId } : {})
      };
      if (activeSubscription?.paddle_subscription_id || activeSubscription) {
        const result = await sendRequest(
          API_PLAN_CHANGE_URL,
          'POST',
          requestBody
        );
        if (result?.effective === 'next_billing_period') {
          setPlanStatusModal({
            status: 'scheduled',
            planName: selectedPlan?.plan_name || 'Selected'
          });
        } else {
          setPendingPlanActivation({
            planId: requestedPlanId,
            billingInterval: requestedBillingInterval,
            offerId: requestedOfferId,
            planName: selectedPlan?.plan_name || 'Selected'
          });
        }
        return;
      }
      const res = await sendRequest(API_PLAN_CHECKOUT_URL, 'POST', requestBody);
      const checkoutUrl = res?.checkout_url || res?.checkoutUrl;
      if (checkoutUrl) {
        if (requestedOfferId)
          window.sessionStorage.setItem(
            'billingCheckoutOfferId',
            requestedOfferId
          );
        rememberCheckoutPlanId(
          window.sessionStorage,
          requestedPlanId,
          requestedBillingInterval
        );
        window.location.assign(checkoutUrl);
      } else {
        toast.error('Failed to create payment screen.');
      }
    } catch (err) {
      const code =
        err?.errorData?.code || err?.response?.data?.code || err?.code;
      if (code === 'BILLING_ADDRESS_REQUIRED') {
        setIsBillingSaved(false);
        toast.error('Please save a complete billing address before checkout.');
      } else if (code === 'SEAT_LIMIT_EXCEEDED') {
        toast.error('Your current seat count exceeds this plan limit.');
      } else if (
        ['ENTERPRISE_OFFER_CHANGED', 'ENTERPRISE_OFFER_UNAVAILABLE'].includes(
          code
        )
      ) {
        toast.error(
          'This Enterprise offer has changed. Return to Plan configuration to select the current offer.'
        );
      } else if (code === 'ENTERPRISE_SELECTION_PENDING') {
        toast.info(
          'Your Enterprise selection is awaiting confirmation. Refresh to check its status.'
        );
      } else {
        toast.error('An error occurred while creating checkout.');
      }
    } finally {
      setButtonLoading(false);
    }
  };

  const selectedPricing = selectedPlan
    ? getPlanPricing(selectedPlan, requestedBillingInterval)
    : null;
  const annualSelected = requestedBillingInterval === 'year';

  const closePlanStatusModal = () => {
    if (planStatusModal?.checkout) {
      setCheckoutHandled(true);
      clearCheckoutSuccessState();
    }
    setPlanStatusModal(null);
  };

  if (initialLoading) {
    return <Loading type="page" />;
  }

  return (
    <>
      <PageTitle>Payment & Billing</PageTitle>
      <PlanStatusModal
        isOpen={Boolean(planStatusModal)}
        planName={planStatusModal?.planName || 'Selected'}
        status={planStatusModal?.status || 'active'}
        onClose={closePlanStatusModal}
      />
      {billingState.payment_action_required && (
        <div className="mb-6 rounded-xl border border-red-300 bg-red-50 p-5 text-red-900 dark:border-red-800 dark:bg-red-900/20 dark:text-red-100">
          <h3 className="text-lg font-bold">
            {billingState.account_status === 'billing_suspended'
              ? 'Billing is suspended'
              : 'Payment action required'}
          </h3>
          <p className="mt-2 text-sm">
            We could not collect a subscription or usage payment. Your billing
            grace period
            {billingState.grace_ends_at
              ? ` ends ${format(new Date(billingState.grace_ends_at), 'MMM dd, yyyy')}.`
              : ' is active.'}
          </p>
          {Number(billingState.outstanding_overage_units || 0) > 0 && (
            <p className="mt-2 text-sm font-medium">
              Outstanding usage: {billingState.outstanding_overage_units}{' '}
              overage unit(s),{' '}
              {formatOutstandingAmount(
                billingState.outstanding_overage_amount_cents
              )}
            </p>
          )}
          <ol className="mt-3 list-decimal space-y-1 pl-5 text-sm">
            <li>Open Paddle using the button below.</li>
            <li>Select the payment method for this subscription.</li>
            <li>Update or replace the payment method in Paddle.</li>
            <li>
              {Number(billingState.outstanding_overage_units || 0) > 0
                ? 'Return here and wait for GitMe’s automatic usage-payment retry and recovery.'
                : 'Return here. Access will recover after Paddle confirms a successful subscription payment.'}
            </li>
          </ol>
          {paymentMethodUpdateUrl && (
            <button
              type="button"
              onClick={() => window.open(paymentMethodUpdateUrl, '_blank')}
              className="mt-4 rounded-lg bg-red-700 px-5 py-2.5 font-semibold text-white hover:bg-red-800"
            >
              Update payment method in Paddle
            </button>
          )}
        </div>
      )}
      {(checkoutSubmitted || pendingPlanActivation) && !checkoutConfirmed && (
        <div className="mb-6 rounded-xl border border-blue-200 bg-blue-50 p-4 text-blue-800 dark:border-blue-800 dark:bg-blue-900/20 dark:text-blue-200">
          <strong>Activating your plan.</strong> We are awaiting Paddle
          confirmation. This page will update as soon as your plan is active.
        </div>
      )}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-6">
          {/* Payment Method Status Card */}
          <div className="relative rounded-2xl border border-stroke bg-gradient-to-br from-white to-gray-50 p-8 shadow-lg dark:border-strokedark dark:from-boxdark dark:to-meta-4/10 hover:shadow-xl transition-shadow duration-300">
            <div className="absolute top-6 right-6">
              <div
                className={`flex h-14 w-14 items-center justify-center rounded-full shadow-lg ${
                  hasCard
                    ? 'bg-gradient-to-br from-green-400 to-green-600 text-white'
                    : 'bg-gradient-to-br from-amber-400 to-orange-500 text-white'
                }`}
              >
                {hasCard ? (
                  <svg
                    className="h-7 w-7"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2.5}
                      d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                ) : (
                  <svg
                    className="h-7 w-7"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2.5}
                      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                    />
                  </svg>
                )}
              </div>
            </div>

            <div className="pr-20">
              <h4 className="text-2xl font-bold text-black dark:text-white mb-3">
                {hasCard ? 'Payment Method Active' : 'No Payment Method'}
              </h4>
              <p className="text-base text-gray-600 dark:text-gray-300 leading-relaxed">
                {hasCard
                  ? `Last updated: ${completedAt ? format(new Date(completedAt), 'MMM dd, yyyy') : 'Unknown'}`
                  : 'Add a payment method to start making transactions.'}
              </p>

              {hasCard && (
                <div className="mt-4 flex items-center gap-2 text-sm text-green-600 dark:text-green-400">
                  <svg
                    className="h-4 w-4"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <span>Ready to process payments</span>
                </div>
              )}
            </div>
          </div>

          {/* Billing Address Form */}
          <div className="rounded-2xl border border-stroke bg-white p-8 shadow-default dark:border-strokedark dark:bg-boxdark">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h4 className="text-xl font-bold text-black dark:text-white mb-1">
                  Billing Address
                </h4>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Required for tax calculations and invoice generation
                </p>
              </div>
              <div className="flex items-center gap-2 text-amber-600 dark:text-amber-400">
                <svg
                  className="h-4 w-4"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                    clipRule="evenodd"
                  />
                </svg>
                <span className="text-xs font-medium">Required</span>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="col-span-2">
                <label className="mb-3 block text-sm font-semibold text-gray-700 dark:text-gray-300">
                  Company Name
                </label>
                <input
                  type="text"
                  name="company_name"
                  value={billingDetails.company_name}
                  onChange={handleInputChange}
                  placeholder="Company or organization name"
                  className="w-full px-3 py-2 border-2 rounded-lg text-sm transition-all duration-200 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-slate-700 dark:border-slate-600 dark:text-slate-100 border-slate-300 dark:border-slate-600 hover:border-slate-400"
                />
              </div>

              <div className="col-span-2">
                <label className="mb-3 block text-sm font-semibold text-gray-700 dark:text-gray-300">
                  Street Address <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="first_line"
                  value={billingDetails.first_line}
                  onChange={handleInputChange}
                  placeholder="Street, neighborhood, and building number"
                  className="w-full px-3 py-2 border-2 rounded-lg text-sm transition-all duration-200 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-slate-700 dark:border-slate-600 dark:text-slate-100 border-slate-300 dark:border-slate-600 hover:border-slate-400"
                />
              </div>

              <div>
                <label className="mb-3 block text-sm font-semibold text-gray-700 dark:text-gray-300">
                  City <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="city"
                  value={billingDetails.city}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border-2 rounded-lg text-sm transition-all duration-200 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-slate-700 dark:border-slate-600 dark:text-slate-100 border-slate-300 dark:border-slate-600 hover:border-slate-400"
                />
              </div>

              <div>
                <label className="mb-3 block text-sm font-semibold text-gray-700 dark:text-gray-300">
                  Postal Code <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="postal_code"
                  value={billingDetails.postal_code}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border-2 rounded-lg text-sm transition-all duration-200 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-slate-700 dark:border-slate-600 dark:text-slate-100 border-slate-300 dark:border-slate-600 hover:border-slate-400"
                />
              </div>

              <div>
                <label className="mb-3 block text-sm font-semibold text-gray-700 dark:text-gray-300">
                  Tax Identifier
                </label>
                <input
                  type="text"
                  name="tax_identifier"
                  value={billingDetails.tax_identifier}
                  onChange={handleInputChange}
                  placeholder="VAT number or tax ID"
                  className="w-full px-3 py-2 border-2 rounded-lg text-sm transition-all duration-200 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-slate-700 dark:border-slate-600 dark:text-slate-100 border-slate-300 dark:border-slate-600 hover:border-slate-400"
                />
              </div>

              <div>
                <label className="mb-3 block text-sm font-semibold text-gray-700 dark:text-gray-300">
                  State/Province
                </label>
                <input
                  type="text"
                  name="region"
                  value={billingDetails.region}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border-2 rounded-lg text-sm transition-all duration-200 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-slate-700 dark:border-slate-600 dark:text-slate-100 border-slate-300 dark:border-slate-600 hover:border-slate-400"
                />
              </div>

              <div>
                <label className="mb-3 block text-sm font-semibold text-gray-700 dark:text-gray-300">
                  Country
                </label>
                <Select
                  inputId="billing-country"
                  name="country_code"
                  options={COUNTRY_OPTIONS}
                  value={
                    COUNTRY_OPTIONS.find(
                      option => option.value === billingDetails.country_code
                    ) || null
                  }
                  onChange={option =>
                    handleInputChange({
                      target: {
                        name: 'country_code',
                        value: option?.value || ''
                      }
                    })
                  }
                  styles={COUNTRY_SELECT_STYLES}
                  isSearchable
                  placeholder="Select a country"
                  noOptionsMessage={() => 'No country found'}
                />
              </div>

              <div className="col-span-2 mt-4">
                <button
                  type="button"
                  onClick={handleSaveAddress}
                  disabled={addressLoading}
                  className="flex w-full items-center justify-center gap-3 rounded-xl bg-gradient-to-r from-green-500 to-green-600 py-4 px-6 font-semibold text-white shadow-lg transition-all duration-200 hover:from-green-600 hover:to-green-700 hover:shadow-xl disabled:from-gray-400 disabled:to-gray-500 disabled:cursor-not-allowed"
                >
                  {addressLoading ? (
                    <Skeleton className="h-4 w-16 rounded-sm bg-white/50" />
                  ) : (
                    <>
                      <svg
                        className="h-5 w-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                      Save Billing Address
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* RIGHT COLUMN: Payment Actions */}
        <div className="space-y-6">
          {selectedPlan && (
            <div className="overflow-hidden rounded-2xl border-2 border-primary bg-primary/5 shadow-default dark:bg-primary/10">
              {annualSelected && !selectedPlan.enterprise_offer && (
                <div className="flex flex-wrap items-center justify-between gap-2 bg-gradient-to-r from-emerald-500 to-cyan-500 px-6 py-3 text-white">
                  <span className="text-xs font-black uppercase tracking-[0.14em]">
                    Annual · 2+ Months Free
                  </span>
                  <span className="rounded-full bg-white/20 px-3 py-1 text-xs font-black">
                    Save{' '}
                    {formatOutstandingAmount(selectedPricing.savings_cents)} /
                    year
                  </span>
                </div>
              )}
              <div className="p-6">
                <p className="text-xs font-semibold uppercase tracking-wider text-primary">
                  Selected plan
                </p>
                <h4 className="mt-2 text-2xl font-bold text-black dark:text-white">
                  {selectedPlan.plan_name}
                </h4>
                <p className="mt-2 text-3xl font-black text-black dark:text-white">
                  {formatOutstandingAmount(
                    selectedPricing.display_monthly_price_cents
                  )}
                  <span className="text-sm font-semibold text-gray-500">
                    {' '}
                    /{' '}
                    {selectedPlan.enterprise_offer?.billing_interval === 'year'
                      ? 'year'
                      : 'month'}
                  </span>
                </p>
                {annualSelected && !selectedPlan.enterprise_offer && (
                  <p className="mt-1 text-sm font-semibold text-emerald-700 dark:text-emerald-300">
                    {formatOutstandingAmount(
                      selectedPricing.checkout_amount_cents
                    )}{' '}
                    billed annually
                  </p>
                )}
                <div className="mt-4 space-y-1 text-sm text-gray-600 dark:text-gray-300">
                  <p>{selectedPlan.seat_limit} seats</p>
                  <p>
                    {(selectedPlan.included_tokens / 1000000).toLocaleString()}M
                    tokens / month
                  </p>
                  <p>
                    {formatOutstandingAmount(
                      selectedPricing.overage_price_cents
                    )}{' '}
                    per started 1M overage tokens
                  </p>
                </div>
                <div className="mt-5 rounded-xl border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-900">
                  <p className="text-xs font-black uppercase tracking-[0.12em] text-slate-400">
                    Due now
                  </p>
                  {previewLoading ? (
                    <p className="mt-2 text-sm font-semibold text-slate-500">
                      Calculating tax and credits…
                    </p>
                  ) : checkoutPreview ? (
                    <>
                      <p className="mt-1 text-3xl font-black text-slate-950 dark:text-white">
                        {checkoutPreview.formatted_total}
                      </p>
                      <div className="mt-2 flex justify-between text-xs text-slate-500">
                        <span>
                          Subtotal {checkoutPreview.formatted_subtotal}
                        </span>
                        <span>Tax {checkoutPreview.formatted_tax}</span>
                      </div>
                      {Number(checkoutPreview.credit_cents || 0) > 0 && (
                        <p className="mt-2 text-xs font-bold text-emerald-600">
                          Credit applied: {checkoutPreview.formatted_credit}
                        </p>
                      )}
                      {checkoutPreview.effective === 'next_billing_period' && (
                        <p className="mt-2 text-xs font-semibold text-amber-600">
                          No plan change is applied today; this amount is due at
                          the next billing period.
                        </p>
                      )}
                    </>
                  ) : (
                    <div className="mt-2">
                      <p className="text-sm font-semibold text-red-600">
                        {previewError ||
                          'Save your billing address to calculate the total.'}
                      </p>
                      {isBillingSaved && (
                        <button
                          type="button"
                          onClick={loadCheckoutPreview}
                          className="mt-2 text-sm font-black text-primary underline"
                        >
                          Retry calculation
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          <div className="rounded-2xl border border-stroke bg-white p-8 shadow-default dark:border-strokedark dark:bg-boxdark">
            <div className="mb-6">
              <h4 className="text-xl font-bold text-black dark:text-white mb-2">
                Payment Methods
              </h4>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Manage your payment methods securely
              </p>
            </div>

            <div className="space-y-4">
              {!requestedPlanId ? (
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
                  <div className="flex items-start gap-2">
                    <svg
                      className="h-4 w-4 text-red-600 dark:text-red-400 mt-0.5 flex-shrink-0"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                        clipRule="evenodd"
                      />
                    </svg>
                    <p className="text-sm text-red-800 dark:text-red-200">
                      <strong>Plan required:</strong> Please select a plan
                      before adding a payment method.
                      <br />
                      <a
                        href="/settings/plan-configuration"
                        className="underline font-medium hover:text-red-700 dark:hover:text-red-300"
                      >
                        Go to plan configuration
                      </a>
                    </p>
                  </div>
                </div>
              ) : (
                !isBillingSaved && (
                  <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3">
                    <div className="flex items-start gap-2">
                      <svg
                        className="h-4 w-4 text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                          clipRule="evenodd"
                        />
                      </svg>
                      <p className="text-sm text-amber-800 dark:text-amber-200">
                        <strong>Billing address required:</strong> Please
                        complete and save your billing address before continuing
                        to checkout.
                      </p>
                    </div>
                  </div>
                )
              )}

              <button
                disabled={
                  buttonLoading ||
                  previewLoading ||
                  !requestedPlanId ||
                  !isBillingSaved ||
                  !checkoutPreview
                }
                onClick={openCardCheckout}
                className={`w-full py-4 px-6 rounded-xl font-semibold text-white shadow-lg transition-all duration-200 ${
                  buttonLoading ||
                  previewLoading ||
                  !requestedPlanId ||
                  !isBillingSaved ||
                  !checkoutPreview
                    ? 'bg-blue-500 cursor-not-allowed opacity-60  '
                    : 'bg-gradient-to-r from-primary to-primary/80 hover:from-primary/90 hover:to-primary shadow-primary/25 hover:shadow-xl'
                }`}
              >
                {buttonLoading ? (
                  <div className="flex items-center justify-center gap-3">
                    <Skeleton className="h-4 w-16 rounded-sm bg-white/50" />
                    <Loading type="inline" />
                  </div>
                ) : hasCard ? (
                  <div className="flex items-center justify-center gap-3">
                    <svg
                      className="h-5 w-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                      />
                    </svg>
                    {activeSubscription
                      ? checkoutPreview?.effective === 'next_billing_period'
                        ? 'Schedule Plan Change'
                        : 'Confirm Plan Change'
                      : 'Continue to Checkout'}
                  </div>
                ) : (
                  <div className="flex items-center justify-center gap-3">
                    <svg
                      className="h-5 w-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                      />
                    </svg>
                    Add Payment Method
                  </div>
                )}
              </button>

              {hasCard && (paymentMethodUpdateUrl || portalUrl) && (
                <button
                  onClick={() =>
                    window.open(paymentMethodUpdateUrl || portalUrl, '_blank')
                  }
                  className="w-full py-3 px-6 rounded-xl font-medium bg-gray-100 hover:bg-gray-200 dark:bg-meta-4 dark:text-white dark:hover:bg-meta-4/80 text-black transition-all duration-200 shadow-md hover:shadow-lg"
                >
                  <div className="flex items-center justify-center gap-3">
                    <svg
                      className="h-4 w-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                      />
                    </svg>
                    Update payment method in Paddle
                  </div>
                </button>
              )}
            </div>
          </div>

          <div className="rounded-2xl border border-stroke bg-gradient-to-br from-green-50 to-emerald-50 p-6 shadow-default dark:border-strokedark dark:from-green-900/20 dark:to-emerald-900/20 dark:bg-boxdark">
            <div className="flex items-center gap-3 text-green-700 dark:text-green-400 mb-4">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-green-100 dark:bg-green-900/30">
                <svg
                  className="h-5 w-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                  />
                </svg>
              </div>
              <span className="font-bold text-lg">Security & Privacy</span>
            </div>
            <p className="text-sm text-green-700 dark:text-green-300 leading-relaxed">
              Your payment information is never stored on our servers. All
              transactions are processed through Paddle&apos;s PCI-DSS compliant
              secure infrastructure.
            </p>

            <div className="mt-4 flex items-center gap-2 text-xs text-green-600 dark:text-green-400">
              <svg className="h-3 w-3" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clipRule="evenodd"
                />
              </svg>
              <span>256-bit SSL encryption</span>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default CardManagement;
